<template>
  <div class="container-fluid">
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Actualizador de precios por lote</h1>
          <p class="view-header__subtitle">Aplica un porcentaje de incremento sobre los productos filtrados.</p>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-8">
        <transition name="fade">
          <div v-if="isCalculating" class="calculating-banner">
            <i class="bi bi-arrow-repeat calculating-banner__spin"></i>
            Recalculando precios...
          </div>
        </transition>
        <div v-if="filtersApplied" class="count-banner">
          <i class="bi bi-funnel-fill"></i>
          Productos filtrados: <strong>{{ filteredCount }}</strong>
          <span class="count-banner__hint">— se impactarán al confirmar la actualización.</span>
        </div>
        <div v-else class="count-banner count-banner--muted">
          <i class="bi bi-info-circle"></i>
          Elegí una lista de precios y aplicá filtros para ver los productos a impactar.
        </div>
        <div class="data-table" :class="{ 'data-table--calculating': isCalculating }">
          <vdtnet-table
            v-if="showTable"
            ref="table"
            :fields="fields"
            :opts="opts"
            :selectCheckbox="0"
            container-class-name="table-responsive"
            class-name="table mb-0"
          />
        </div>
      </div>

      <div class="col-md-4">
        <ActualizarProducto
          :filtered-count="filteredCount"
          @filters="applyFilters"
          @filters-cleared="onFiltersCleared"
          @increase-change="updateIncrease"
        />
      </div>
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import ActualizarProducto from './Actualizar-producto.vue';
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";

export default {
  components: {
    VdtnetTable,
    ActualizarProducto,
  },
  mixins: [formatMoneyMixin],
  data: function () {
    return {
      showTable: false,
      filtersApplied: false,
      filteredCount: 0,
      isCalculating: false,
      increase: {
        percentage: 0,
        multiple: 10,
      },
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/products/listdt",
          type: "POST",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            priceList_id: '',
            provider_id: 'all',
            category_id: 'all',
            brand_id: 'all',
          },
        },
        serverSide: true,
        autoWidth: false,
      },
      fields: {
        id: { visible: false },
        name: {
          label: "Producto",
          searchable: true,
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        stock: { label: "Stock actual", align: "right" },
        price: {
          label: "Precio actual",
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            return data ? this.formatoDinamicoMoneda(data) : '';
          },
        },
        new_price: {
          label: "Precio nuevo",
          data: 'price',
          align: "right",
          render: (data, type) => {
            if (type !== 'display') return data;
            if (!data) return '';
            const base = parseFloat(data);
            const total = base + (base / 100) * this.increase.percentage;
            const m = Number(this.increase.multiple) || 0;
            const rounded = m > 0 ? Math.round(total / m) * m : total;
            return `<span class="data-table__primary">${this.formatoDinamicoMoneda(rounded)}</span>`;
          },
        },
      },
    };
  },
  watch: {
    showTable(v) {
      if (v) this.$nextTick(() => this.bindTableEvents());
    },
  },
  created() {
    const vm = this;
    this.opts.drawCallback = function () {
      const info = this.api().page.info();
      vm.filteredCount = info ? info.recordsDisplay : 0;
    };
  },
  methods: {
    bindTableEvents() {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isCalculating = true; });
      dt.on('xhr.dt', () => { this.isCalculating = false; });
    },
    applyFilters(value) {
      this.opts.ajax.data = value;
      this.filtersApplied = true;
      // Re-mount the table so DataTables picks up the new ajax.data payload
      // (it's captured at init, so reload() alone wouldn't send the new filters).
      this.showTable = false;
      this.$nextTick(() => {
        this.showTable = true;
      });
    },
    onFiltersCleared() {
      this.filtersApplied = false;
      this.filteredCount = 0;
      this.showTable = false;
    },
    updateIncrease(data) {
      Object.assign(this.increase, data);
      // Re-render local: el "Precio nuevo" se calcula client-side, no hace falta xhr.
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (dt) dt.rows().invalidate('data').draw(false);
    },
  },
};
</script>

<style scoped>
.calculating-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  margin-bottom: 8px;
  background: rgba(31, 110, 200, 0.08);
  color: #1f6ec8;
  border: 1px solid rgba(31, 110, 200, 0.18);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
}

.count-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: rgba(40, 167, 69, 0.08);
  color: #1c7c3a;
  border: 1px solid rgba(40, 167, 69, 0.20);
  border-radius: 8px;
  font-size: 13px;
}

.count-banner__hint {
  color: #4d6b56;
  font-weight: 400;
}

.count-banner--muted {
  background: rgba(108, 117, 125, 0.08);
  color: #4d5258;
  border-color: rgba(108, 117, 125, 0.18);
}

.calculating-banner__spin {
  display: inline-block;
  animation: calc-spin 0.8s linear infinite;
}

@keyframes calc-spin {
  to { transform: rotate(360deg); }
}

.data-table--calculating {
  position: relative;
}

.data-table--calculating::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.18s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
